import React, { useState } from 'react';
import { X, Play } from 'lucide-react';
import { galleryCategories, contactInfo } from '../data/mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const Gallery = () => {
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Extract YouTube video ID from URL
  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Extract Vimeo video ID from URL
  const getVimeoId = (url) => {
    const regExp = /vimeo\.com\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  // Render media in lightbox
  const renderMediaPlayer = (media) => {
    if (!media) return null;

    switch (media.type) {
      case 'youtube':
        const youtubeId = getYouTubeId(media.url);
        return (
          <div className="w-full max-w-5xl aspect-video">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
              title={media.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded-sm"
            ></iframe>
          </div>
        );

      case 'vimeo':
        const vimeoId = getVimeoId(media.url);
        return (
          <div className="w-full max-w-5xl aspect-video">
            <iframe
              src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1`}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              className="rounded-sm"
            ></iframe>
          </div>
        );

      case 'video':
        return (
          <div className="w-full max-w-5xl">
            <video
              controls
              autoPlay
              className="w-full rounded-sm"
            >
              <source src={media.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'image':
      default:
        return (
          <img
            src={media.url}
            alt={media.title}
            className="max-w-full max-h-[80vh] object-contain rounded-sm"
          />
        );
    }
  };

  return (
    <section id="gallery" className="py-24 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="heading-font text-5xl md:text-6xl font-bold text-white mb-4">
            Our Work
          </h2>
          <div className="w-24 h-1 gold-bg mx-auto mb-6"></div>
          <p className="body-font text-xl text-gray-300 max-w-3xl mx-auto">
            A glimpse into the extraordinary experiences we've created for events across India
          </p>
        </div>

        {/* Categorized Gallery with Tabs */}
        <Tabs defaultValue={galleryCategories[0].id} className="w-full">
          <TabsList className="grid w-full grid-cols-1 md:grid-cols-3 gap-4 mb-12 bg-transparent h-auto p-0">
            {galleryCategories.map((category) => (
              <TabsTrigger
                key={category.id}
                value={category.id}
                className="relative overflow-hidden rounded-sm h-40 md:h-48 data-[state=active]:ring-4 data-[state=active]:ring-yellow-500 transition-all group cursor-pointer border-2 border-transparent hover:border-yellow-500/50"
              >
                <img
                  src={category.thumbnail}
                  alt={category.name}
                  className="absolute inset-0 w-full h-full object-cover brightness-[0.35] group-hover:brightness-50 data-[state=active]:brightness-60 transition-all"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <span className="relative z-10 heading-font text-lg md:text-xl font-bold text-white drop-shadow-2xl px-4 text-center">
                  {category.name}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

          {galleryCategories.map((category) => (
            <TabsContent key={category.id} value={category.id} className="mt-0">
              {/* Gallery Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.items.map((media) => {
                  const isVideo = media.type === 'youtube' || media.type === 'vimeo' || media.type === 'video';
                  const thumbnailUrl = media.type === 'image' ? media.url : media.thumbnail;

                  return (
                    <div
                      key={media.id}
                      className="group relative overflow-hidden rounded-sm cursor-pointer aspect-[4/3] bg-zinc-900"
                      onClick={() => setSelectedMedia(media)}
                    >
                      {/* Thumbnail/Image */}
                      <img
                        src={thumbnailUrl}
                        alt={media.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      
                      {/* Video Play Button Overlay */}
                      {isVideo && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="gold-bg w-20 h-20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                            <Play className="text-black ml-1" size={32} fill="black" />
                          </div>
                        </div>
                      )}

                      {/* Info Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-70 transition-all duration-300 flex flex-col items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center p-6">
                          <p className="body-font text-sm gold-accent font-semibold mb-2">
                            {media.category}
                          </p>
                          <h3 className="heading-font text-xl text-white font-bold">
                            {media.title}
                          </h3>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="body-font text-gray-400 mb-6">
            Want to see more of our work?
          </p>
          <a 
            href={contactInfo.socialMedia.youtube}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 gold-bg text-black px-8 py-4 rounded-sm font-semibold body-font hover:bg-yellow-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            Watch All Our Work on YouTube
          </a>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedMedia && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-yellow-400 transition-colors z-10"
            onClick={() => setSelectedMedia(null)}
          >
            <X size={32} />
          </button>
          <div 
            className="w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {renderMediaPlayer(selectedMedia)}
            <div className="text-center mt-6">
              <p className="body-font text-sm gold-accent font-semibold mb-2">
                {selectedMedia.category}
              </p>
              <h3 className="heading-font text-2xl text-white font-bold">
                {selectedMedia.title}
              </h3>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
